import { TournamentType } from 'config/types/tournament'
import Column from 'layout/Components/Column'
import { RowBetween, RowMiddle } from 'layout/Components/Row'
import { Trans, useTranslation } from 'react-i18next'
import { useAuth } from 'state/auth/hooks'
import styled from 'styled-components'
import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import Image from 'UIKit/Image'
import Text from 'UIKit/Text'
import TransText from 'UIKit/Text/TransText'
import { formatDisplayDateTime } from 'utils/dateHelper'
import ActionButton, { InvalidAction } from './ActionButton'
import TournamentCoundown from './TournamentCoundown'

type TournamentCardProps = {
  tournamentBase: TournamentType
  updateJoinTournament: (id: string) => void
}

const TournamentCard: React.FC<TournamentCardProps> = ({ tournamentBase, updateJoinTournament }) => {
  const { id, expiredTime, image, maxEligibleVIPLevel, minEligibleVIPLevel, name, isJoined, isProviderTournament } =
    tournamentBase
  const { isSigned } = useAuth()
  const isExpired = Date.now() > expiredTime
  const { t } = useTranslation()

  return (
    <StyledTournamentCard p="20px 28px" style={{ filter: isExpired ? 'grayscale(100%)' : '0' }} key={id}>
      {tournamentBase && (
        <>
          <RowMiddle height={isSigned ? 'fit-content' : '100%'}>
            <RowBetween alignItems="flex-start !important">
              <Box size="80px" overflow="hidden">
                <Image width={80} height={80} src={image} alt="tournament logo" />
              </Box>

              <Column ml="8px" justifyContent="space-between !important" style={{ flex: 1 }} height="104px">
                <Box minHeight="40px">
                  <TransText fontSize="16px" fontWeight={800} lineHeight="normal" letterSpacing="-0.32px">
                    {name}
                  </TransText>
                </Box>
                <Box>
                  <RowBetween py="8px" borderBottom="1px solid #353c47">
                    <TransText
                      fontSize="12px"
                      fontWeight={500}
                      color="textSubtle"
                      lineHeight="normal"
                      letterSpacing="-0.24px"
                    >
                      {isExpired ? 'Finished at' : 'Finish in'}
                    </TransText>
                    <Text fontSize="12px" fontWeight={500} color="rgba(250, 250, 250, 0.3)" lineHeight="normal">
                      {isExpired ? (
                        formatDisplayDateTime(new Date(expiredTime))
                      ) : (
                        <TournamentCoundown expiredTime={expiredTime} />
                      )}
                    </Text>
                  </RowBetween>

                  <RowBetween mt="8px">
                    <StyledPrizePoolText
                      fontSize={['12px', , '14px']}
                      fontWeight={500}
                      color="textSubtle"
                      lineHeight="normal"
                      letterSpacing="-0.24px"
                      whiteSpace="nowrap"
                    >
                      <Trans>Prize Pool</Trans>
                    </StyledPrizePoolText>
                    <Flex>
                      <Text
                        fontSize={tournamentBase.prizePool?.length > 16 ? '14px' : '16px'}
                        fontWeight={800}
                        letterSpacing={-0.32}
                        whiteSpace="nowrap"
                      >
                        {tournamentBase.prizePool}
                      </Text>
                    </Flex>
                  </RowBetween>
                </Box>
              </Column>
            </RowBetween>
          </RowMiddle>

          {isSigned && (
            <>
              {isProviderTournament ? (
                <InvalidAction content={isExpired ? t('Finished') : t('Auto Joined')} />
              ) : (
                <ActionButton
                  minEligibleVIPLevel={minEligibleVIPLevel}
                  maxEligibleVIPLevel={maxEligibleVIPLevel}
                  updateJoinTournament={updateJoinTournament}
                  isJoined={isJoined}
                  tournamentId={id}
                  isExpired={isExpired}
                />
              )}
            </>
          )}
        </>
      )}
    </StyledTournamentCard>
  )
}

const StyledTournamentCard = styled(Column)`
  background-image: url('/images/tournament/card-background.png');
  background-size: cover;
  border-radius: 12px;
  overflow: hidden;
  height: 100%;
  justify-content: space-between;
`

const StyledPrizePoolText = styled(Text)`
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSubtle};
  line-height: normal;
  letter-spacing: -0.24px;
  background: linear-gradient(180deg, #f96a7b 43.35%, #c82832 138.04%);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-right: 6px;
`

export default TournamentCard
